<template>
  <div>
    <clip-loader v-if="isLoading" />
    <Panel v-else :title="panelTitle" class="position-relative">
      <div>
        <ExportView
          :data-available-exports="availableExports"
          :data-form-errors="errors"
          :data-available-centers="localAvailableCenters"
          :data-available-regions="localAvailableRegions"
          :data-available-products="availableProducts"
          :data-role-features="roleFeatures"
          :is-exporting="isExporting"
          :is-downloading="isDownloading"
          :latest-export-status="latestExportStatus"
          @export-data="handleExport"
          @download-export="downloadExport"
        />
      </div>
    </Panel>
  </div>
</template>

<script>
import Panel from "../../../../components/Layout/Panel.vue";
import ExportView from "./ExportView.vue";
import contentDisposition from "content-disposition";
import { shortDateFormat } from "../../../../helpers/platform";
import { practitionerHttpService } from "../../store/practitioner-http-service";
import ExportExcludeRegionCenterDateToggleProductForm from "./filter/ExportExcludeRegionCenterDateToggleProductForm.vue";
import ExportProductRegionCenterAndYearToggleForm from "./filter/ExportProductRegionCenterAndYearToggleForm.vue";
import ExportProductRegionToggleProductForm from "./filter/ExportProductRegionToggleProductForm.vue";
import ExportRegionCenterDateToggleForm from "./filter/ExportRegionCenterDateToggleForm.vue";
import ExportAccessLogForm from "./filter/ExportAccessLogForm.vue";
import ExportWithoutOptionsForm from "./filter/ExportWithoutOptionsForm.vue";
import moment from "moment";

// These filter components are now sent together with their linked exports from the backend.
// The mapping is available at Shared/ExportController.php.
const formComponentMap = {
  ExportExcludeRegionCenterDateToggleProductForm,
  ExportProductRegionToggleProductForm,
  ExportRegionCenterDateToggleForm,
  ExportAccessLogForm,
  ExportProductRegionCenterAndYearToggleForm,
  ExportWithoutOptionsForm,
};

export default {
  name: "ExportLogic",
  components: { Panel, ExportView },
  props: {
    panelTitle: { type: String, default: "" },
    exportUrl: { type: String, default: "" },
    loadData: { type: Function, default: null },
    availableCenters: { type: Array, default: null },
    availableRegions: { type: Array, default: null },
    role: { type: String, default: "" },
  },
  data() {
    return {
      isLoading: false,
      isExporting: false,
      isDownloading: false,
      latestExportStatus: null,
      errors: {},
      blobResponse: null,
      localAvailableCenters: this.availableCenters || [],
      localAvailableRegions: this.availableRegions || [],
      availableExports: [],
      availableProducts: [],
      roleFeatures: {
        showExcludeInactiveCenters: false,
        showExcludeCentersWithTestInName: false,
        showExcludeTestPatients: false,
      },
      role: this.role,
      initialPollDelay: 10000,
      secondaryPollDelay: 30000,
    };
  },
  watch: {
    availableCenters(val) {
      this.localAvailableCenters = val || [];
    },
    availableRegions(val) {
      this.localAvailableRegions = val || [];
    },
    blobResponse(val) {
      if (val) this.handleBlobDownload(val);
    },
  },
  mounted() {
    this.isLoading = true;
    this.loadFormSettings();
    if (this.loadData) {
      this.loadData(this.updateState);
    }
    this.pollForExportStatus(this.updateState);
  },
  methods: {
    updateState(obj) {
      Object.keys(obj).forEach((k) => {
        if (k === "availableCenters") this.localAvailableCenters = obj[k];
        else if (k === "availableRegions") this.localAvailableRegions = obj[k];
        else this[k] = obj[k];
      });
    },
    handleExport(formData) {
      this.isExporting = true;
      this.errors = {};
      this.updateState({ latestExportStatus: "queued" });

      // Standardize date fields
      const format = shortDateFormat();
      if (formData.get("fromDate")) {
        formData.set(
          "fromDate",
          moment(formData.get("fromDate"), format).format("YYYY-MM-DD"),
        );
      }
      if (formData.get("untilDate")) {
        formData.set(
          "untilDate",
          moment(formData.get("untilDate"), format).format("YYYY-MM-DD"),
        );
      }

      practitionerHttpService
        .post(this.exportUrl, formData)
        .then(() => {
          setTimeout(() => {
            this.pollForExportStatus(this.updateState);
          }, this.initialPollDelay);
        })
        .catch((error) => {
          this.errors = error.response?.data || {};
          this.isExporting = false;
          this.updateState({ latestExportStatus: null });
          this.userErrorMessage?.(error);
        });
    },
    loadFormSettings: function () {
      practitionerHttpService
        .get("/api/internal/exports/settings/" + this.role)
        .then((response) => {
          this.availableExports = response.data.forms.map((item) => ({
            ...item,
            name: this.trans(item.name),
            form:
              formComponentMap[item.formComponent] || ExportWithoutOptionsForm,
          }));
          this.availableProducts = response.data.products || [];
          this.roleFeatures = response.data.roleFeatures ?? {
            showExcludeInactiveCenters: false,
            showExcludeCentersWithTestInName: false,
            showExcludeTestPatients: false,
          };
        })
        .catch((error) => {
          this.updateState({ latestExportStatus: "failed" });
          this.errors = error.response?.data || {};
          this.isExporting = false;
          this.userErrorMessage?.(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    pollForExportStatus: function (update) {
      practitionerHttpService
        .get(`/api/internal/exports/poll`)
        .then((response) => {
          switch (response.status) {
            case 201:
            case 202:
              update({
                latestExportStatus:
                  response.status === 201 ? "queued" : "processing",
              });
              update({ isExporting: true });
              setTimeout(() => {
                this.pollForExportStatus(update);
              }, this.secondaryPollDelay);
              break;
            case 204:
              update({ isExporting: false });
              update({ latestExportStatus: "none" });
              break;
            default:
              update({ isExporting: true });
              update({ latestExportStatus: "completed" });
          }
        })
        .catch((error) => {
          update({ latestExportStatus: "failed" });
          update({ errors: error.response?.data, isExporting: false });
        });
    },
    downloadExport: function () {
      this.updateState({ isDownloading: true });
      practitionerHttpService
        .get(`/api/internal/exports/download`, { responseType: "blob" })
        .then((response) => {
          if (
            response.data instanceof Blob &&
            response.data.type !== "application/json"
          ) {
            this.updateState({ blobResponse: response });
            this.updateState({ latestExportStatus: "none" });
          }
          this.updateState({ isExporting: false });
          this.updateState({ isDownloading: false });
        })
        .catch((error) => {
          this.updateState({ latestExportStatus: "failed" });
          this.updateState({
            errors: error.response?.data,
            isExporting: false,
          });
          this.updateState({ isDownloading: false });
          this.userErrorMessage(error);
        });
    },
    handleBlobDownload(response) {
      const blob = response.data;
      const fileName = contentDisposition.parse(
        response.headers["content-disposition"],
      ).parameters.filename;

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      }

      const data = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = data;
      link.download = fileName;
      link.click();

      setTimeout(function () {
        window.URL.revokeObjectURL(data);
      }, 100);
    },
  },
};
</script>
